import { Outlet, createFileRoute, redirect, useParams } from '@tanstack/react-router'

import { useActivityStream } from '@/features/activity/api'
import { useReportActivity } from '@/features/presence/api'
import { useAppliedTheme } from '@/features/theme/useAppliedTheme'
import { AppShell } from '@/features/lounges/AppShell'
import { meQueryOptions } from '@/features/auth/api'
import { myLoungesQueryOptions } from '@/features/lounges/api'

/**
 * The signed-in half of the app.
 *
 * A pathless layout route, so every page under it inherits the guard and the
 * shell without any of them repeating either. The guard runs in `beforeLoad`,
 * which is before the child's loader — a signed-out visitor never fires a query
 * that was only going to 401.
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context: { queryClient }, location }) => {
    const { me } = await queryClient.ensureQueryData(meQueryOptions())

    if (!me) {
      throw redirect({
        to: '/login',
        // So signing in returns you to where you were headed, rather than
        // dumping you on the home page having forgotten why you came.
        search: { redirect: location.href },
      })
    }

    // Handed down to children, so a component that needs the current user does
    // not have to re-read the cache and handle a null it cannot get.
    return { me }
  },
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(myLoungesQueryOptions()),
  component: AppLayout,
})

function AppLayout() {
  return (
    <AppShell>
      <ActivityRealtime />
      <Outlet />
    </AppShell>
  )
}

/**
 * Holds the app-wide badge subscription and renders nothing.
 *
 * Here rather than in the lounge layout, because the whole point is hearing
 * about rooms in lounges you are *not* looking at. Its own component, the same
 * shape `LoungeRealtime` has one level down, so a re-render of the shell cannot
 * churn the subscription.
 *
 * It sits inside `_app`, which only renders once `beforeLoad` has confirmed a
 * session — so no socket is opened for a signed-out visitor.
 */
function ActivityRealtime() {
  // `strict: false` because this sits above every route and most of them have
  // no `nookId` at all. The same call `ConnectedElsewhere` already makes.
  const openNookId = useParams({ strict: false }).nookId
  useActivityStream(openNookId)
  // The one thing this browser may assert about its own presence: that it is
  // being used. Here rather than in the lounge layout because it is a fact about
  // the *session*, not about the room you happen to have open.
  useReportActivity()
  useAppliedTheme()
  return null
}
